import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const V1 = '#7F00FF';
const V2 = '#B163FF';
const V3 = '#51158C';

/* ─── ICONS ───────────────────────────────────────────────── */
const ChevronLeftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const ClockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const ArrowRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);

/* ─── CATEGORY IMAGES ─────────────────────────────────────── */
const getCategoryImg = (category = '') => {
  const c = category.toLowerCase();
  if (c.includes('web') || c.includes('react') || c.includes('javascript') || c.includes('frontend'))
    return 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=700&q=80';
  if (c.includes('python') || c.includes('data') || c.includes('ml') || c.includes('ai'))
    return 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=700&q=80';
  if (c.includes('design') || c.includes('ui') || c.includes('ux') || c.includes('figma'))
    return 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=700&q=80';
  if (c.includes('devops') || c.includes('cloud') || c.includes('docker'))
    return 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=700&q=80';
  if (c.includes('mobile') || c.includes('android') || c.includes('flutter'))
    return 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=700&q=80';
  if (c.includes('cyber') || c.includes('security'))
    return 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=700&q=80';
  if (c.includes('backend') || c.includes('node') || c.includes('server'))
    return 'https://images.unsplash.com/photo-1555099962-4199c345e5dd?w=700&q=80';
  return 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=700&q=80';
};

const levelColor = {
  Beginner:     { text: '#0dba79', bg: 'rgba(13,186,121,0.15)'  },
  Intermediate: { text: '#f5a623', bg: 'rgba(245,166,35,0.15)'  },
  Advanced:     { text: '#f04155', bg: 'rgba(240,65,85,0.15)'   },
};

const AUTO_DELAY = 3500; // ms

/* ─── COURSE CAROUSEL ─────────────────────────────────────── */
const FeaturedCoursesSection = ({ apiCourses, loading = false }) => {
  const [active, setActive] = useState(0);
  const pausedRef = useRef(false);

  const list = apiCourses && apiCourses.length > 0 ? apiCourses : [];
  const total = list.length;
  const totalRef = useRef(total);
  useEffect(() => { totalRef.current = total; }, [total]);

  const next = () => setActive(a => (a + 1) % totalRef.current);
  const prev = () => setActive(a => (a - 1 + totalRef.current) % totalRef.current);

  /* Stable interval — never clears due to hover */
  useEffect(() => {
    if (total < 2) return;
    const timer = setInterval(() => {
      if (!pausedRef.current) {
        setActive(a => (a + 1) % totalRef.current);
      }
    }, AUTO_DELAY);
    return () => clearInterval(timer);
  }, [total]); // only restart if course count changes

  const getPos = (i) => {
    const offset = ((i - active + total) % total);
    return offset > total / 2 ? offset - total : offset;
  };

  /* ─── SKELETON CAROUSEL (shown while loading) ─── */
  if (loading) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 60, flexWrap: 'wrap', gap: 20 }}>
          <div>
            <div className="section-label">COURSES</div>
            <h2 style={{ fontSize: 'clamp(30px,4vw,52px)', fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 8 }}>
              Courses Built for<br />
              <span style={{ background: `linear-gradient(135deg,${V1},${V2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Real Careers</span>
            </h2>
            <p style={{ color: 'var(--muted)', fontSize: 15, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${V1}33`, borderTopColor: V1, animation: 'spin 0.8s linear infinite', display: 'inline-block', flexShrink: 0 }} />
              Loading courses...
            </p>
          </div>
        </div>

        {/* Skeleton carousel stage */}
        <div style={{ position: 'relative', height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          {[
            { transform: 'translateX(calc(-72% - 12px)) scale(0.87)', opacity: 0.4, zIndex: 8 },
            { transform: 'translateX(0) scale(1)',                     opacity: 1,   zIndex: 10 },
            { transform: 'translateX(calc(72% + 12px)) scale(0.87)',  opacity: 0.4, zIndex: 8 },
          ].map((stl, i) => (
            <div key={i} style={{
              position: 'absolute',
              width: '38%', minWidth: 280, maxWidth: 420,
              borderRadius: 24, overflow: 'hidden',
              border: `1px solid ${i === 1 ? `${V1}55` : 'var(--border)'}`,
              background: 'var(--bg2)',
              boxShadow: i === 1 ? `0 28px 70px rgba(0,0,0,0.45), 0 0 0 1px ${V1}33` : 'none',
              ...stl,
            }}>
              {/* Skeleton image area */}
              <div className="skeleton-bone" style={{ height: 220, borderRadius: 0 }} />
              {/* Skeleton content */}
              <div style={{ padding: '16px 22px 22px' }}>
                <div className="skeleton-bone" style={{ width: '75%', height: 18, marginBottom: 14 }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div className="skeleton-bone" style={{ width: 80, height: 14 }} />
                  <div className="skeleton-bone" style={{ width: 60, height: 24 }} />
                </div>
                <div className="skeleton-bone" style={{ width: '100%', height: 42, borderRadius: 12 }} />
              </div>
            </div>
          ))}
        </div>

        {/* Skeleton dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 32 }}>
          {[0,1,2,3,4].map(i => (
            <div key={i} className="skeleton-bone" style={{
              width: i === 1 ? 28 : 8, height: 8, borderRadius: 99,
            }} />
          ))}
        </div>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 48, flexWrap: 'wrap', gap: 20 }}>
          <div>
            <div className="section-label">COURSES</div>
            <h2 style={{ fontSize: 'clamp(30px,4vw,52px)', fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 8 }}>
              Courses Built for<br />
              <span style={{ background: `linear-gradient(135deg,${V1},${V2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Real Careers</span>
            </h2>
          </div>
          <Link to="/courses" className="btn btn-outline">View All Courses</Link>
        </div>
        <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--muted)', fontSize: 15 }}>
          <div style={{ marginBottom: 16, fontSize: 24 }}>No courses yet</div>
          <Link to="/courses" style={{ color: V2, fontWeight: 700 }}>Browse all courses →</Link>
        </div>
      </div>
    );
  }

  return (
    <div
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 60, flexWrap: 'wrap', gap: 20 }}>
        <div>
          <div className="section-label">COURSES</div>
          <h2 style={{ fontSize: 'clamp(30px,4vw,52px)', fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 8 }}>
            Courses Built for<br />
            <span style={{ background: `linear-gradient(135deg,${V1},${V2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Real Careers</span>
          </h2>
          <p style={{ color: 'var(--muted)', fontSize: 15 }}>
            {active + 1} of {total} courses — auto-switching every few seconds
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {[{ fn: prev, Icon: ChevronLeftIcon }, { fn: next, Icon: ChevronRightIcon }].map(({ fn, Icon }, i) => (
            <button key={i} onClick={fn} style={{
              width: 48, height: 48, borderRadius: '50%',
              background: 'var(--bg3)', border: '1.5px solid var(--border-strong)',
              color: 'var(--text)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = V1; e.currentTarget.style.borderColor = V1; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text)'; }}
            >
              <Icon />
            </button>
          ))}
        </div>
      </div>

      {/* Stage */}
      <div style={{ position: 'relative', height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {list.map((c, i) => {
          const pos = getPos(i);
          let stl = { zIndex: 1, transform: 'scale(0.5)', opacity: 0, pointerEvents: 'none' };
          if (pos === 0)  stl = { zIndex: 10, transform: 'translateX(0) scale(1)',                    opacity: 1,    filter: 'none',               pointerEvents: 'auto' };
          if (pos === 1)  stl = { zIndex: 8,  transform: 'translateX(calc(72% + 12px)) scale(0.87)',  opacity: 0.72, filter: 'brightness(0.6)',     pointerEvents: 'auto' };
          if (pos === -1) stl = { zIndex: 8,  transform: 'translateX(calc(-72% - 12px)) scale(0.87)', opacity: 0.72, filter: 'brightness(0.6)',     pointerEvents: 'auto' };
          if (pos === 2)  stl = { zIndex: 6,  transform: 'translateX(calc(136% + 20px)) scale(0.74)', opacity: 0.35, filter: 'brightness(0.45)',    pointerEvents: 'none' };
          if (pos === -2) stl = { zIndex: 6,  transform: 'translateX(calc(-136% - 20px)) scale(0.74)',opacity: 0.35, filter: 'brightness(0.45)',    pointerEvents: 'none' };

          const img   = getCategoryImg(c.category);
          const lv    = levelColor[c.level] || levelColor.Beginner;
          const isActive = i === active;

          return (
            <div
              key={c._id || i}
              onClick={() => !isActive && setActive(i)}
              style={{
                position: 'absolute',
                width: '38%', minWidth: 280, maxWidth: 420,
                borderRadius: 24, overflow: 'hidden',
                border: `1px solid ${isActive ? `${V1}55` : 'var(--border)'}`,
                transition: 'all 0.5s cubic-bezier(0.34,1.1,0.64,1)',
                cursor: isActive ? 'default' : 'pointer',
                boxShadow: isActive ? `0 28px 70px rgba(0,0,0,0.45), 0 0 0 1px ${V1}33` : 'none',
                background: 'var(--bg2)',
                ...stl,
              }}
            >
              {/* Image */}
              <div style={{ position: 'relative', height: 220, overflow: 'hidden', flexShrink: 0 }}>
                <img src={img} alt={c.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                {/* Diagonal cut */}
                <div style={{
                  position: 'absolute', bottom: -1, left: 0, right: 0, height: 56,
                  background: 'var(--bg2)',
                  clipPath: 'polygon(0 70%, 100% 0%, 100% 100%, 0% 100%)',
                }} />
                {/* Category badge */}
                {c.category && (
                  <div style={{
                    position: 'absolute', top: 12, left: 12,
                    background: 'var(--img-badge-bg)', backdropFilter: 'blur(8px)',
                    border: `1px solid ${V1}44`,
                    borderRadius: 999, padding: '4px 12px',
                    fontSize: 10, fontWeight: 800, color: 'var(--badge-pill-text)',
                    letterSpacing: '0.07em', textTransform: 'uppercase',
                  }}>{c.category}</div>
                )}
                {/* Level badge */}
                {c.level && (
                  <div style={{
                    position: 'absolute', top: 12, right: 12,
                    background: lv.bg, backdropFilter: 'blur(8px)',
                    borderRadius: 999, padding: '4px 12px',
                    fontSize: 10, fontWeight: 700, color: lv.text,
                  }}>{c.level}</div>
                )}

              </div>

              {/* Content */}
              <div style={{ padding: '0 22px 22px' }}>
                <h3 style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 10, color: 'var(--text)', lineHeight: 1.2 }}>
                  {c.title}
                </h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--muted)', fontSize: 13, fontWeight: 600 }}>
                    <ClockIcon /> {c.duration}
                  </span>
                  <span style={{ fontFamily: "'Inter',sans-serif", fontWeight: 900, fontSize: 22, color: 'var(--text)' }}>
                    ₹{Number(c.fee).toLocaleString()}
                  </span>
                </div>
                <Link
                  to={`/courses/${c._id}`}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    padding: '12px', borderRadius: 12,
                    background: `linear-gradient(135deg,${V1},${V3})`,
                    color: '#fff', fontSize: 14, fontWeight: 700,
                    fontFamily: "'Satoshi','DM Sans',sans-serif",
                    transition: 'opacity 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  View Course <ArrowRightIcon />
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dots */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 32 }}>
        {list.map((_, i) => (
          <button key={i} onClick={() => setActive(i)} style={{
            width: i === active ? 28 : 8, height: 8, borderRadius: 99,
            border: 'none', cursor: 'pointer', padding: 0,
            background: i === active ? `linear-gradient(90deg,${V1},${V2})` : 'var(--border-strong)',
            transition: 'all 0.3s ease',
          }} />
        ))}
      </div>

      <div style={{ textAlign: 'center', marginTop: 28 }}>
        <Link to="/courses" className="btn btn-outline">View All Courses →</Link>
      </div>

      <style>{`
        @keyframes progressBar {
          from { width: 0% }
          to   { width: 100% }
        }
      `}</style>
    </div>
  );
};

export default FeaturedCoursesSection;
